import { authHeaders } from '../lib/apiFetch';
import { podkladyNavrhu } from './rozbor';
/**
 * Valuation Calculator API, v10
 * Mapuje wizardState na ValuationRequest (v10 backend) a volá /api/calculate
 */

const API_BASE = process.env.REACT_APP_API_BASE || '';

function num(val) {
  const n = Number(val);
  return isNaN(n) ? 0 : n;
}

function numOrNull(val, unknown) {
  if (unknown) return null;
  const n = Number(val);
  return n > 0 ? n : null;
}

export function buildValuationRequest(state) {
  const { finance, kpi, traffic_brand, qualitative, segment_id } = state;

  const hasBalanceSheet =
    finance.showBalanceSheet &&
    num(finance.total_assets) > 0;

  return {
    // Typ podnikání rozhoduje na backendu o volbě metody ocenění.
    typ_podnikani: state.typ_podnikani || null,
    // Výkaz ze závěrky a odpovědi klienta: z nich server sestaví návrh
    // hodnoty, tedy hlavní číslo pro obrazovku, zprávu, AI i chat.
    podklady: podkladyNavrhu(state),
    // Zadání vlastními slovy a komentáře k závěrkám. Do výpočtu nevstupují,
    // AI zpráva z nich bere účel ocenění a souvislosti.
    zadani: state.zadani && (state.zadani.text || (state.zadani.soubory || []).length)
      ? {
          text: state.zadani.text || '',
          podklady: (state.zadani.soubory || []).map(s => ({ nazev: s.name || '', komentar: s.komentar || '' })),
        }
      : null,
    finance: {
      revenue: num(finance.revenue),
      revenue_prev_year: num(finance.revenue_prev_year),
      cogs: num(finance.cogs),
      opex: num(finance.opex),
      inventory: num(finance.inventory) || 0,
      revenue_last_3m: num(finance.revenue_last_3m) > 0 ? num(finance.revenue_last_3m) : null,
      revenue_last_3m_prev_year: num(finance.revenue_last_3m_prev_year) > 0 ? num(finance.revenue_last_3m_prev_year) : null,
      balance_sheet: hasBalanceSheet
        ? {
            net_income: num(finance.net_income),
            total_assets: num(finance.total_assets),
            equity: num(finance.equity),
            current_liabilities: num(finance.current_liabilities),
            total_liabilities: num(finance.total_liabilities),
            // Úvěry a hotovost jdou zvlášť. O ně se liší hodnota provozu
            // od toho, co majitel při prodeji dostane. Prázdné pole se
            // posílá jako null, ne nula: nula by tvrdila, že dluh není.
            interest_bearing_debt: finance.interest_bearing_debt === '' || finance.interest_bearing_debt == null
              ? null : num(finance.interest_bearing_debt),
            cash: finance.cash === '' || finance.cash == null
              ? null : num(finance.cash),
            real_estate_book: num(finance.real_estate_book) > 0 ? num(finance.real_estate_book) : null,
            real_estate_market: num(finance.real_estate_market) > 0 ? num(finance.real_estate_market) : null,
          }
        : null,
      // Adjusted EBITDA z kroku Očištění zisku (null = použij vypočtenou EBITDA)
      ebitda_override: finance.ebitda_override != null ? num(finance.ebitda_override) : null,
      // Kamenná pobočka
      has_physical_stores: finance.has_physical_stores || false,
      physical_store_count: num(finance.physical_store_count) > 0 ? num(finance.physical_store_count) : null,
      physical_store_revenue_pct: num(finance.physical_store_revenue_pct) > 0 ? num(finance.physical_store_revenue_pct) : null,
      physical_store_costs: num(finance.physical_store_costs) > 0 ? num(finance.physical_store_costs) : null,
      // Normalizace EBITDA
      normalize_ebitda: finance.normalize_ebitda || false,
      owner_salary_actual: num(finance.owner_salary_actual) > 0 ? num(finance.owner_salary_actual) : null,
      owner_salary_market: num(finance.owner_salary_market) > 0 ? num(finance.owner_salary_market) : null,
      personal_expenses_through_business: num(finance.personal_expenses_through_business) > 0 ? num(finance.personal_expenses_through_business) : null,
      one_time_costs: num(finance.one_time_costs) > 0 ? num(finance.one_time_costs) : null,
      owner_operates: finance.owner_operates !== false,
    },

    kpi: {
      rrs: kpi.rrs_unknown ? null : (num(kpi.rrs) >= 0 && kpi.rrs !== '' ? num(kpi.rrs) : null),
      monthly_ad_spend: numOrNull(kpi.monthly_ad_spend, kpi.monthly_ad_spend_unknown),
      roas: numOrNull(kpi.roas, kpi.roas_unknown),
      ltv: numOrNull(kpi.ltv, kpi.ltv_unknown),
      cac: numOrNull(kpi.cac, kpi.cac_unknown),
      repeat_purchase_rate: numOrNull(kpi.repeat_purchase_rate, kpi.repeat_purchase_rate_unknown),
    },

    traffic_brand: {
      // Výrobě ani službám se krok s návštěvností nezobrazuje, takže tyhle
      // dvě pole zůstanou prázdná. Backend zná hodnotu "unknown" a počítá
      // s ní, prázdný řetězec ale odmítne a celá valuace spadne na 422.
      traffic_mix: traffic_brand.traffic_mix || 'unknown',
      brand_mix: traffic_brand.brand_mix || 'unknown',
      channel_dependency: traffic_brand.channel_dependency || 'diversified',
    },

    qualitative: {
      segment_id: segment_id || qualitative.segment_id,
      own_production_pct: num(qualitative.own_production_pct),
      own_brand_pct: num(qualitative.own_brand_pct),
      private_label_pct: num(qualitative.private_label_pct),
      resale_pct: num(qualitative.resale_pct),
      fulfillment: qualitative.fulfillment || undefined,
      founder_dependency: qualitative.founder_dependency || undefined,
      total_sku_count: qualitative.total_sku_count !== '' && qualitative.total_sku_count != null
        ? num(qualitative.total_sku_count) || null
        : null,
      product_concentration_pct: num(qualitative.product_concentration_pct),
      customer_concentration_pct: qualitative.customer_concentration_pct === '' || qualitative.customer_concentration_pct == null
        ? null : num(qualitative.customer_concentration_pct),
      single_supplier_revenue_pct: num(qualitative.single_supplier_revenue_pct),
      market_entry_barriers: qualitative.market_entry_barriers || undefined,
      product_consumability: qualitative.product_consumability || undefined,
      business_age_years: qualitative.business_age_years !== '' && qualitative.business_age_years != null
        ? num(qualitative.business_age_years) || null
        : null,
      revenue_trend: qualitative.revenue_trend || 'stable_growth',
    },
  };
}

/**
 * Chyba z backendu čitelně.
 *
 * FastAPI vrací u neplatného vstupu pole objektů. Když se poslalo rovnou
 * do Error, klient četl "[object Object],[object Object]" a nedozvěděl se
 * nic. Tohle z něj udělá větu, která říká, které pole vadí.
 */
export function popisChyby(chyba, stav) {
  const detail = chyba && chyba.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;

  if (Array.isArray(detail) && detail.length) {
    const vety = detail.slice(0, 3).map((polozka) => {
      const pole = Array.isArray(polozka.loc)
        ? polozka.loc.filter(x => x !== 'body').join('.')
        : '';
      const duvod = polozka.msg || 'neplatná hodnota';
      return pole ? `${pole}: ${duvod}` : duvod;
    });
    const zbytek = detail.length > 3 ? ` a ${detail.length - 3} dalších` : '';
    return `Neplatné vstupní údaje (${vety.join('; ')}${zbytek}).`;
  }

  return `Server odpověděl chybou ${stav}.`;
}

export async function calculateValuation(wizardState) {
  try {
    const body = buildValuationRequest(wizardState);

    // Plný výsledek (podklady PRO sekcí) server pošle jen k zaplacené valuaci.
    const id = wizardState.savedValuationId;
    const q = id ? `?valuation_id=${encodeURIComponent(id)}` : '';
    const response = await fetch(`${API_BASE}/api/calculate${q}`, {
      method: 'POST',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(popisChyby(err, response.status));
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error('Valuation API error:', error);
    return { success: false, error: error.message };
  }
}
